/*=========================================================================================
  File Name: store.js
  Description: Vuex store
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import { PRODUCTS } from "@clientProducts";
import { cloneDeep } from "lodash";
import Vue from "vue";
import Vuex from "vuex";
import { vuexfireMutations } from "vuexfire";

import config from "@/config";

import actions from "./actions";
import getters from "./getters";
import mutations from "./mutations";
import state from "./state";

Vue.use(Vuex);

// All products
// Auth
import moduleAuth from "./auth/moduleAuth.js";
import moduleAllProducts from "./modules/allProducts.js";
// Assets
import moduleAssets from "./modules/assets.js";
// Benchmarks
import moduleBenchmarks from "./modules/benchmarks.js";
// Key numbers
import moduleKeyNumbers from "./modules/keyNumbers.js";
// Operations
import moduleOperations from "./modules/operations.js";
// Platform settings
import modulePlatformSettings from "./modules/platformSettings.js";
// User information
import moduleUserInfo from "./modules/userInfo.js";
// Users
import moduleUsers from "./modules/users.js";

class ImportError extends Error {}

/* CUSTOM SECTIONS */
// Use try/catch with require ensures we don't break if the file doesn't exist.
let moduleCustomSections;
try {
  // You can also alias this folder if you want absolute security
  moduleCustomSections = require(
    `@/store/modules/customSections/${process.env.VUE_APP_ORG_ID}.js`
  ).default;
} catch (e) {
  // No custom section for this client, ignore.
}

/* PRODUCTS */

// Create products modules
const loadProductModule = (productName) => {
  try {
    // We use the virtual alias @clientStoreFolder.
    return require(`@clientStoreFolder/products/${productName}.js`).default;
  } catch (e) {
    try {
      // Fallback to client generic products.
      return require(`@clientStoreFolder/products.js`).default;
    } catch (e) {
      throw new ImportError(`Unable to import product module '${productName}'`);
    }
  }
};

const allProductsModules = PRODUCTS.map((productName) => {
  try {
    let moduleProduct = cloneDeep(loadProductModule(productName));
    return [productName, moduleProduct];
  } catch (err) {
    // If no special product module found, load the generic product module.
    if (err instanceof ImportError) {
      // Need to deep clone otherwise the different modules will share the same state.
      let moduleProduct = cloneDeep(require("@/store/modules/products.js").default);
      return [productName, moduleProduct];
    } else {
      throw err;
    }
  }
});
const mapProductsModules = new Map(allProductsModules);

const modules = {
  allProducts: moduleAllProducts,
  assets: moduleAssets,
  auth: moduleAuth,
  benchmarks: moduleBenchmarks,
  keyNumbers: moduleKeyNumbers,
  platformSettings: modulePlatformSettings,
  userInfo: moduleUserInfo,
  users: moduleUsers,
  operations: moduleOperations,
  ...Object.fromEntries(mapProductsModules),
};

// Only add custom sections module if defined.
if (moduleCustomSections) {
  modules["customSections"] = moduleCustomSections;
}

// Only add investment proposal module if activated for this org.
if (config.investmentProposal) {
  modules["investmentProposal"] = require("@/store/modules/investmentProposal.js").default;
}

export default new Vuex.Store({
  getters,
  mutations: {
    ...mutations,
    ...vuexfireMutations,
  },
  state,
  actions,
  modules,
  strict: process.env.VUE_APP_ALQUANT_ENV !== "production",
  // plugins: [progressiveLoader]
});
